/**
 * Promo code API client.
 *
 * Admin creates codes (e.g. LAUNCH20 = 20% off first month).
 * Users apply at checkout. One-time use per user.
 * Integrates with Stripe coupons on the backend.
 */
import { apiGet, apiPost, apiPatch, apiDelete } from "./client.ts";

export interface PromoCode {
  id: string;
  code: string;               // e.g. "LAUNCH20"
  discountType: "percent" | "fixed";
  discountValue: number;       // 20 (%) or 500 (cents)
  maxRedemptions: number | null;
  currentRedemptions: number;
  expiresAt: string | null;
  isActive: boolean;
  stripeCouponId: string | null;
  createdAt: string;
}

export interface PromoValidation {
  valid: boolean;
  code: string;
  discountType: "percent" | "fixed";
  discountValue: number;
  message: string;
}

export const promosApi = {
  /** Validate a promo code (user-facing, no auth required for validation). */
  validate: (code: string) =>
    apiPost<PromoValidation>("/promos/validate", { code }),

  /** Apply a promo code to the current user's next subscription payment. */
  apply: (code: string) =>
    apiPost<{ ok: boolean; discount: string }>("/promos/apply", { code }),

  /** Admin: list all promo codes. */
  adminList: () =>
    apiGet<{ promos: PromoCode[] }>("/admin/promos"),

  /** Admin: create a new promo code. */
  adminCreate: (data: {
    code: string;
    discountType: "percent" | "fixed";
    discountValue: number;
    maxRedemptions?: number;
    expiresAt?: string;
  }) => apiPost<{ ok: boolean; promo: PromoCode }>("/admin/promos", data),

  /** Admin: update a promo code. */
  adminUpdate: (id: string, data: Partial<{
    isActive: boolean;
    maxRedemptions: number | null;
    expiresAt: string | null;
  }>) => apiPatch<{ ok: boolean }>(`/admin/promos/${id}`, data),

  /** Admin: delete a promo code. */
  adminDelete: (id: string) =>
    apiDelete<{ ok: boolean }>(`/admin/promos/${id}`),
};
